import { IntegrationCategory } from './integration-type.model';

/** Integration type codes the UI has specific forms/settings for. */
export type StoreType =
  | 'SHOPIFY'
  | 'WOOCOMMERCE'
  | 'SAMSCLUB'
  | 'CIN7'
  | 'CUSTOM'
  | 'LOOP'
  | 'RETURNZAP'
  | 'REDO';

/**
 * True for returns integrations (Loop, ReturnZap, Redo, ...). Uses the
 * category the API attaches to the store from the integration_types catalog
 * (GET /store/:uuid), rather than a hardcoded list of types.
 */
export function isReturnsStore(
  store: Pick<Store, 'category'> | null | undefined,
): boolean {
  return store?.category === IntegrationCategory.RETURNS;
}

export class Store {
  uuid: string;
  name: string;
  url: string;
  type: StoreType;
  /** From integration_types; set by the API on GET /store/:uuid. */
  category?: IntegrationCategory;
  clientId: string;
  sourceId: string;
  state: 'ACTIVE' | 'PENDING' | 'DISABLED';
  lastOrderSync: Date;
  lastShipmentSync: Date;
  lastInventorySync: Date;
  lastReturnSync: Date;

  createdAt: Date;
  updatedAt: Date;
  config: StoreConfig;
  auth: StoreAuth;

  public setStore(_store: any) {
    Object.assign(this, _store);
  }
}

export interface StoreConfig {
  connection?: boolean;
  orderSync?: OrderSyncConfig;
  shipmentSync?: ShipmentSyncConfig;
  fulfillmentSync?: FulfillmentSyncConfig;
  inventorySync?: InventorySyncConfig;
  returnSync?: ReturnSyncConfig;
  orderFilterEnabled?: boolean;
  itemFilterEnabled?: boolean;
  samsClubKeys?: SamsClubKeys;
  cin7Auth?: Cin7Auth;
  customAuth?: CustomAuth;
  /** Loop credential. Redo is Loop-compatible and uses this too (API contract). */
  loopAuth?: LoopAuth;
  returnzapAuth?: ReturnZapAuth;
  username?: string;
  password?: string;
  wooCommerceKeys?: WooCommerceKeys;
}

export interface CustomAuth {
  apiKey: string;
}

export interface Cin7Auth {
  accountId: string;
  apiKey: string;
}

export interface LoopAuth {
  apiKey: string;
}

/** Field name must match the API's ReturnZapAuth DTO. */
export interface ReturnZapAuth {
  bearerToken: string;
}

export interface ReturnSyncConfig {
  enabled: boolean;
  syncStartDate?: string;
  lastSyncDate?: string;
  enableReturnLambda?: boolean;
  returnFilterEnabled?: boolean;
  returnStatus?: string[];
}

export interface FulfillmentServiceConfig {
  locationId: string;
  enabled: boolean;
  name: string;
}
export interface OrderSyncConfig {
  enabled: boolean;
  syncStartDate?: Date;
  transactionType?: string;
  orderStatus?: string;
  customStatus?: string;
  isConsolidated?: boolean;
  /** @deprecated Use `StoreConfig.fulfillmentSync.enabled`. Kept in sync. */
  shipmentEnabled?: boolean;
  acknowledgementEnabled?: boolean;
  shipNodeId?: string;
  enableFutureFiltering?: boolean;
  shipNodes?: { shipNodeId: string; shipNodeName: string }[];
  selectedShipNodeId?: string;
  fulfillmentService?: FulfillmentServiceConfig;
  filterByLocation?: {
    atomixLocationId?: string;
    enabled: boolean;
  };
}
export interface ShipmentSyncConfig {
  enabled: boolean;
  shipmentType?: string;
}
/**
 * Controls whether fulfillment events are synced back to the source platform,
 * covering both creations and updates.
 *
 * Supersedes the legacy `OrderSyncConfig.shipmentEnabled`. Both locations are
 * read and written during the transition; see
 * `shared/utils/fulfillment-sync.util.ts`.
 */
export interface FulfillmentSyncConfig {
  enabled: boolean;
}

export interface InventorySyncConfig {
  enabled: boolean;
  locationId?: string;
  inventoryType?: string;
  enabledSkus?: {
    [sku: string]: string[];
  };
  disabledSkus?: {
    [locationId: string]: string[];
  };
}

export interface StoreAuth {
  state: string;
  access_token: string;
  authorization_code: string;
  scope: string;
}

export interface StoreCreateRequest {
  name: string;
  type: string;
  url: string;
  clientId: string;
  sourceId?: string;

  config: StoreConfig;

  shopifyCode?: string;
  hmac?: string;

  incomingTransactionType?: string;
  outgoingShipmentType?: string;
  inventorySyncType?: string;
}

export interface SamsClubKeys {
  clientId: string;
  clientSecret?: string;
  supplierId: string;
}
export interface WooCommerceKeys {
  consumerKey: string;
  consumerSecret: string;
}
